import type { Branding } from '@pm/schemas';
import { cn } from '@pm/ui';
import { useMe } from '@/lib/auth';
import { brandInitials, hasLogo, useBranding } from '@/lib/branding';

/**
 * Logotipo do tema ativo. Os dois <img> ficam no DOM e o CSS escolhe pelo `data-theme` do
 * <html>, então trocar o tema não recarrega nada. Sem logotipo, não renderiza.
 */
export function BrandLogo({
  branding,
  size = 'sm',
  className,
}: {
  branding: Branding | null | undefined;
  /** sm/lg: altura fixa (28/40px). fill: ocupa a largura do contêiner, até 64px de altura. */
  size?: 'sm' | 'lg' | 'fill';
  className?: string;
}) {
  const dark = branding?.logos.dark ?? branding?.logos.light;
  const light = branding?.logos.light ?? branding?.logos.dark;
  if (!branding || !dark || !light) return null;
  const fill = size === 'fill';
  const h = fill ? '' : size === 'lg' ? 'h-10 max-h-10' : 'h-7 max-h-7';
  const img = fill
    ? 'h-auto max-h-16 w-full object-contain object-center'
    : 'w-auto max-w-full object-contain object-left';
  return (
    <span
      className={cn('flex min-w-0 items-center', fill && 'w-full justify-center', h, className)}
      title={branding.display_name}
    >
      <img
        src={dark.url}
        alt={branding.display_name}
        className={cn(img, h, 'in-data-[theme=light]:hidden')}
      />
      <img
        src={light.url}
        alt={branding.display_name}
        className={cn(img, h, 'hidden in-data-[theme=light]:block')}
      />
    </span>
  );
}

/** Monograma usado quando não há logotipo (ou o espaço é pequeno demais para ele). */
export function BrandMonogram({ name, className }: { name: string; className?: string }) {
  return (
    <span
      className={cn(
        'grid size-[22px] shrink-0 place-items-center rounded bg-accent font-mono text-[11px] font-semibold text-accent-fg',
        className,
      )}
    >
      {brandInitials(name)}
    </span>
  );
}

/**
 * Marca no topo da sidebar. Com logotipo: o do tema ativo. Sem logotipo ou com a sidebar
 * recolhida: monograma + nome.
 */
export function BrandMark({ collapsed }: { collapsed: boolean }) {
  const me = useMe();
  const { data } = useBranding();
  const name = data?.display_name ?? me.organization?.name ?? 'Project Manager';

  if (hasLogo(data) && !collapsed)
    return <BrandLogo branding={data} size="fill" className="flex-1 px-1 py-1.5" />;
  return (
    <>
      <span title={collapsed ? name : undefined} aria-hidden={!collapsed}>
        <BrandMonogram name={name} />
      </span>
      {!collapsed ? (
        <span className="truncate text-base font-semibold text-text-1">{name}</span>
      ) : null}
    </>
  );
}
